import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type FC, type KeyboardEvent, type MouseEvent } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  RemoveFormatting,
  Baseline,
  Highlighter,
} from 'lucide-react';
import { Popover } from 'antd';
import {
  isRichTextEmpty,
  normalizeRichTextForSave,
  toEditorHtml,
  richTextToPlain,
  sanitizeRichText,
} from '../../utils/richText';
import './IpcRichTextEditor.css';

export type IpcRichTextEditorProps = {
  value?: string | null;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** inline=单元格备注；block=进度同步 */
  variant?: 'inline' | 'block';
  className?: string;
  id?: string;
  onChange?: (html: string) => void;
  onFocus?: () => void;
  onBlur?: (html: string) => void;
};

type Cmd =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikeThrough'
  | 'insertUnorderedList'
  | 'insertOrderedList'
  | 'removeFormat';

const TEXT_COLORS = [
  '#1a1a1a',
  '#475569',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0d9488',
  '#2563eb',
  '#7c3aed',
  '#db2777',
];

const HIGHLIGHT_COLORS = [
  { label: '无', value: 'transparent' },
  { label: '黄', value: '#fef08a' },
  { label: '橙', value: '#fed7aa' },
  { label: '红', value: '#fecaca' },
  { label: '绿', value: '#bbf7d0' },
  { label: '青', value: '#a5f3fc' },
  { label: '蓝', value: '#bfdbfe' },
  { label: '紫', value: '#e9d5ff' },
  { label: '粉', value: '#fbcfe8' },
];

function runCommand(cmd: Cmd, value?: string) {
  document.execCommand(cmd, false, value);
}

function applyForeColor(color: string) {
  document.execCommand('styleWithCSS', false, 'true');
  document.execCommand('foreColor', false, color);
}

function applyHighlight(color: string) {
  document.execCommand('styleWithCSS', false, 'true');
  // Chrome: hiliteColor；部分环境回退 backColor
  const ok = document.execCommand('hiliteColor', false, color);
  if (!ok) document.execCommand('backColor', false, color);
}

/** 只读展示富文本（兼容历史纯文本） */
export const IpcRichTextView: FC<{
  value?: string | null;
  className?: string;
  emptyText?: string;
}> = ({ value, className, emptyText = '—' }) => {
  if (isRichTextEmpty(value)) {
    return <span className={className}>{emptyText}</span>;
  }
  return (
    <span
      className={['ipc-rich-text-view', className].filter(Boolean).join(' ')}
      title={richTextToPlain(value)}
      dangerouslySetInnerHTML={{ __html: toEditorHtml(value) }}
    />
  );
};

export const IpcRichTextEditor: FC<IpcRichTextEditorProps> = ({
  value,
  placeholder = '请输入…',
  disabled = false,
  autoFocus = false,
  variant = 'block',
  className,
  id,
  onChange,
  onFocus,
  onBlur,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastExternalRef = useRef<string>('');
  const savedRangeRef = useRef<Range | null>(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = toEditorHtml(value);
    if (next === lastExternalRef.current && el.innerHTML === next) return;
    if (document.activeElement === el) return;
    el.innerHTML = next;
    lastExternalRef.current = next;
  }, [value]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [autoFocus, disabled]);

  const emitHtml = () => {
    const el = editorRef.current;
    if (!el) return '';
    const html = normalizeRichTextForSave(el.innerHTML);
    lastExternalRef.current = html;
    return html;
  };

  const handleInput = () => {
    const html = emitHtml();
    onChange?.(html);
  };

  const handleBlur = () => {
    // 点工具栏/色板时不要立刻当失焦保存
    window.setTimeout(() => {
      const root = editorRef.current?.closest('.ipc-rich-text');
      if (root && root.contains(document.activeElement)) return;
      if (colorOpen || highlightOpen) return;
      const html = emitHtml();
      onBlur?.(html);
    }, 0);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const el = editorRef.current;
    if (!el || !el.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    const el = editorRef.current;
    if (!range || !el) return;
    el.focus();
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const handleToolbarMouseDown = (cmd: Cmd) => (e: MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    editorRef.current?.focus();
    restoreSelection();
    runCommand(cmd);
    handleInput();
    saveSelection();
  };

  const applyColor = (color: string) => {
    if (disabled) return;
    restoreSelection();
    applyForeColor(color);
    handleInput();
    saveSelection();
    setColorOpen(false);
  };

  const applyBg = (color: string) => {
    if (disabled) return;
    restoreSelection();
    applyHighlight(color);
    handleInput();
    saveSelection();
    setHighlightOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (variant === 'inline' && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      editorRef.current?.blur();
    }
  };

  /** 不支持图片：粘贴时去掉 img，仅保留文本与允许的格式 */
  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    const cd = e.clipboardData;
    if (!cd) return;

    const html = cd.getData('text/html');
    const text = cd.getData('text/plain');

    if (html) {
      const cleaned = sanitizeRichText(html);
      if (cleaned) {
        document.execCommand('insertHTML', false, cleaned);
      } else if (text) {
        document.execCommand('insertText', false, text);
      }
    } else if (text) {
      document.execCommand('insertText', false, text);
    }

    handleInput();
    saveSelection();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.some(f => f.type.startsWith('image/'))) {
      e.preventDefault();
    }
  };

  const iconSize = variant === 'inline' ? 12 : 14;
  const empty = isRichTextEmpty(value);
  const rootClass = [
    'ipc-rich-text',
    `ipc-rich-text--${variant}`,
    disabled ? 'is-disabled' : '',
    empty ? 'is-empty' : '',
    className || '',
  ].filter(Boolean).join(' ');

  const colorPanel = (
    <div className="ipc-rich-text__swatches" onMouseDown={e => e.preventDefault()}>
      {TEXT_COLORS.map(c => (
        <button
          key={c}
          type="button"
          className="ipc-rich-text__swatch"
          style={{ background: c }}
          title={c}
          onClick={() => applyColor(c)}
        />
      ))}
      <label className="ipc-rich-text__custom-color" title="自定义颜色">
        <input
          type="color"
          defaultValue="#2563eb"
          onChange={e => applyColor(e.target.value)}
        />
      </label>
    </div>
  );

  const highlightPanel = (
    <div className="ipc-rich-text__swatches" onMouseDown={e => e.preventDefault()}>
      {HIGHLIGHT_COLORS.map(c => (
        <button
          key={c.value}
          type="button"
          className={[
            'ipc-rich-text__swatch',
            c.value === 'transparent' ? 'is-clear' : '',
          ].filter(Boolean).join(' ')}
          style={c.value === 'transparent' ? undefined : { background: c.value }}
          title={c.label}
          onClick={() => applyBg(c.value)}
        />
      ))}
    </div>
  );

  return (
    <div className={rootClass}>
      {!disabled && (
        <div className="ipc-rich-text__toolbar" role="toolbar" aria-label="格式">
          <button type="button" className="ipc-rich-text__btn" title="加粗" onMouseDown={handleToolbarMouseDown('bold')}>
            <Bold size={iconSize} aria-hidden />
          </button>
          <button type="button" className="ipc-rich-text__btn" title="斜体" onMouseDown={handleToolbarMouseDown('italic')}>
            <Italic size={iconSize} aria-hidden />
          </button>
          <button type="button" className="ipc-rich-text__btn" title="下划线" onMouseDown={handleToolbarMouseDown('underline')}>
            <Underline size={iconSize} aria-hidden />
          </button>
          <button type="button" className="ipc-rich-text__btn" title="删除线" onMouseDown={handleToolbarMouseDown('strikeThrough')}>
            <Strikethrough size={iconSize} aria-hidden />
          </button>
          <span className="ipc-rich-text__sep" aria-hidden />
          <Popover
            trigger="click"
            open={colorOpen}
            onOpenChange={open => {
              if (open) saveSelection();
              setColorOpen(open);
            }}
            content={colorPanel}
            placement="bottomLeft"
            arrow={false}
          >
            <button
              type="button"
              className="ipc-rich-text__btn"
              title="文字颜色"
              onMouseDown={e => {
                e.preventDefault();
                saveSelection();
              }}
            >
              <Baseline size={iconSize} aria-hidden />
            </button>
          </Popover>
          <Popover
            trigger="click"
            open={highlightOpen}
            onOpenChange={open => {
              if (open) saveSelection();
              setHighlightOpen(open);
            }}
            content={highlightPanel}
            placement="bottomLeft"
            arrow={false}
          >
            <button
              type="button"
              className="ipc-rich-text__btn"
              title="背景色"
              onMouseDown={e => {
                e.preventDefault();
                saveSelection();
              }}
            >
              <Highlighter size={iconSize} aria-hidden />
            </button>
          </Popover>
          <span className="ipc-rich-text__sep" aria-hidden />
          <button type="button" className="ipc-rich-text__btn" title="无序列表" onMouseDown={handleToolbarMouseDown('insertUnorderedList')}>
            <List size={iconSize} aria-hidden />
          </button>
          <button type="button" className="ipc-rich-text__btn" title="有序列表" onMouseDown={handleToolbarMouseDown('insertOrderedList')}>
            <ListOrdered size={iconSize} aria-hidden />
          </button>
          <span className="ipc-rich-text__sep" aria-hidden />
          <button type="button" className="ipc-rich-text__btn" title="清除格式" onMouseDown={handleToolbarMouseDown('removeFormat')}>
            <RemoveFormatting size={iconSize} aria-hidden />
          </button>
        </div>
      )}
      <div
        id={id}
        ref={editorRef}
        className="ipc-rich-text__editor"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        onInput={handleInput}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
      />
    </div>
  );
};
